import { useState, useCallback, useMemo, useRef } from 'react'
import { useAtomValue } from 'jotai'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import {
    ActionButton,
    ErrorMessages,
    LoginModal,
    FieldCell,
    renderFieldRows,
    buildInitialForm,
    validatePasswordStrength,
    type FieldRenderContext,
    type CountryItem
} from '@/core/components'
import { useCurrentLanguage } from '@/core/utils'
import { TermsDialog } from '@/core/components/TermsAndConsent/terms-dialog.component'
import { ReCaptchaV2Modal } from '@/core/recaptcha/recaptcha-v2-modal.component'
import { useRecaptchaGate } from '@/core/recaptcha/use-recaptcha-gate.hook'
import { useOmanCheckout } from '../../../hooks/useOmanCheckout'
import { useRegionsByCountry } from '../../../hooks/useRegionsByCountry'
import { useNavigateToShipping } from '../../payments/hooks/useNavigateToShipping'
import { fgUseComplexPasswordAtom, fgTermAndConditionsAtom } from '@/core/feelgreat/feelgreat.store'
import { omanCountryListAtom, isLoadingOmanGeoAtom } from '../../../stores/oman.store'
import { guestAccountSections, guestAgreementFields, omanConfig } from '../../../oman.config'
import { SectionCard } from './MemberShippingStep'
import type { TOmanCheckoutForm } from '../../../types/oman.types'

const GuestCreateAccountStep = () => {
    const { translate } = useTranslate()
    const currentLanguage = useCurrentLanguage()
    const { loading, errors, setErrors, duplicatedEmail, duplicatedPhone, submitGuestForm } = useOmanCheckout()
    const goToShipping = useNavigateToShipping()
    const countryList = useAtomValue(omanCountryListAtom)
    const geoLoading = useAtomValue(isLoadingOmanGeoAtom)
    const useComplexPassword = useAtomValue(fgUseComplexPasswordAtom)
    const termAndConditions = useAtomValue(fgTermAndConditionsAtom)

    const [showPassword, setShowPassword] = useState(false)
    const [termsOpen, setTermsOpen] = useState(false)
    const [loginOpen, setLoginOpen] = useState(false)
    const { isVerifying: recaptchaVerifying, runWithRecaptcha, modalProps: recaptchaModalProps } = useRecaptchaGate()
    const formDisabled = loading || recaptchaVerifying

    const handleLinkClick = useCallback((fieldKey: string) => {
        if (fieldKey === 'pdpa') setTermsOpen(true)
    }, [])

    const [form, setForm] = useState<TOmanCheckoutForm>(() =>
        buildInitialForm<TOmanCheckoutForm>([...guestAccountSections, { title: '', fields: guestAgreementFields }])
    )

    const { regions, regionsLoading } = useRegionsByCountry(form.country)

    // See MemberShippingStep for the rationale: per-key handler cache keeps
    // `set('email')` returning the same function ref across renders so the
    // memoised `FieldCell` can bail out for unchanged fields.
    const handlersRef = useRef<Record<string, (e: React.ChangeEvent<HTMLInputElement>) => void>>({})
    const set = useCallback((key: string) => {
        if (!handlersRef.current[key]) {
            handlersRef.current[key] = e => setForm(prev => ({ ...prev, [key]: e.target.value }))
        }
        return handlersRef.current[key]
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (useComplexPassword) {
            const passwordErrors = validatePasswordStrength(form.password ?? '')
            if (passwordErrors.length > 0) {
                setErrors(passwordErrors)
                return
            }
        }
        await runWithRecaptcha('checkout', () => submitGuestForm(form), omanConfig.features.checkout.recaptcha)
    }

    const allAgreementsChecked = guestAgreementFields.every(field => Boolean((form as unknown as Record<string, unknown>)[field.key]))

    const mappedRegions = useMemo(() => regions.map(r => ({ name: r.name, id: r.value })), [regions])

    const getCountryDisplayName = useCallback(
        (item: CountryItem) => (currentLanguage === 'AR' && item.name_native ? item.name_native : item.name_english),
        [currentLanguage]
    )

    const fieldCtx = useMemo<FieldRenderContext>(
        () => ({
            form: form as unknown as Record<string, unknown>,
            setForm,
            set,
            formDisabled,
            dialCode: omanConfig.dialCode,
            countryList,
            getCountryDisplayName,
            geoLoading,
            regions: mappedRegions,
            regionsLoading,
            showPassword,
            setShowPassword,
            useComplexPassword,
            onLinkClick: handleLinkClick,
            testIdPrefix: 'guest-create-account-step'
        }),
        [
            form,
            set,
            formDisabled,
            countryList,
            getCountryDisplayName,
            geoLoading,
            mappedRegions,
            regionsLoading,
            showPassword,
            useComplexPassword,
            handleLinkClick
        ]
    )

    return (
        <>
            <ReCaptchaV2Modal {...recaptchaModalProps} />
            <Stack component="form" onSubmit={handleSubmit} spacing={3} data-testid="guest-create-account-step-stack-handle-submit">
                {guestAccountSections.map(section => (
                    <SectionCard key={section.title} data-testid="guest-create-account-step-section-card">
                        <Typography sx={{ fontSize: 32, fontWeight: 700, color: '#003764', mb: 3 }} data-testid="guest-create-account-step-typography-1">
                            {translate(section.title)}
                        </Typography>
                        <Stack spacing={2.5} data-testid="guest-create-account-step-stack-1">
                            {renderFieldRows(section.fields, fieldCtx)}
                        </Stack>

                        {(duplicatedEmail || duplicatedPhone) && (
                            <Box
                                sx={{
                                    mt: '30px',
                                    px: 3,
                                    py: 1.5,
                                    borderRadius: '8px',
                                    backgroundColor: '#FDDEDE',
                                    textAlign: 'center'
                                }}
                                data-testid="guest-create-account-step-box-typography">
                                <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ fontSize: 14, color: '#FE5D7C', fontWeight: 400 }}
                                    data-testid="guest-create-account-step-typography-2">
                                    {duplicatedPhone ? translate('duplicated_email_or_phone_and_go_login') : translate('duplicated_email_and_go_login')}{' '}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="span"
                                    onClick={() => setLoginOpen(true)}
                                    sx={{ fontSize: 14, fontWeight: 700, color: '#FE5D7C', textDecoration: 'underline', cursor: 'pointer' }}
                                    data-testid="guest-create-account-step-typography-login">
                                    {translate('login')}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ fontSize: 14, color: '#FE5D7C' }}
                                    data-testid="guest-create-account-step-typography-3">
                                    ?
                                </Typography>
                            </Box>
                        )}

                        <ErrorMessages list={errors} sx={{ mt: '30px' }} data-testid="guest-create-account-step-error-messages" />

                        <Stack mt={2} spacing={1} data-testid="guest-create-account-step-stack-2">
                            {guestAgreementFields.map(field => (
                                <FieldCell key={field.key} field={field} ctx={fieldCtx} data-testid="guest-create-account-step-field-cell" />
                            ))}
                        </Stack>

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }} data-testid="guest-create-account-step-box-action-button-submit">
                            <ActionButton
                                text={formDisabled ? '' : translate('create_your_account')}
                                type="submit"
                                disabled={formDisabled || !allAgreementsChecked}
                                startIcon={
                                    formDisabled ? (
                                        <CircularProgress size={22} color="inherit" data-testid="guest-create-account-step-circular-progress" />
                                    ) : undefined
                                }
                                data-testid="guest-create-account-step-action-button-submit"
                            />
                        </Box>
                    </SectionCard>
                ))}
            </Stack>
            <TermsDialog
                open={termsOpen}
                onClose={() => setTermsOpen(false)}
                title={termAndConditions?.title}
                htmlContent={termAndConditions?.htmlContent}
                data-testid="guest-create-account-step-terms-dialog"
            />
            <LoginModal
                open={loginOpen}
                onClose={() => setLoginOpen(false)}
                onSuccess={() => goToShipping()}
                data-testid="guest-create-account-step-login-modal"
            />
        </>
    )
}

export default GuestCreateAccountStep
